import type {
  ActivitiesConfigVersionFormat,
  InsuranceProduct,
  InsuranceProductVersion,
  ProductVersionFormat,
} from '../../model/model.ts'

import type { NlRcphAccountantSubSegment, NlRcphSegment } from './nl-rcph-activity-configuration.ts'
import type { NlRcphPolicyItems, NlRcphPolicyItemsState } from './nl-rcph-insurance.ts'
import type { NlRcphPricingVersion } from './nl-rcph-pricing-versions.ts'
import { nlRcphAccountantSubSegmentShortLabel, nlRcphSegmentShortLabel } from './nl-rcph-segment-short-label.ts'
import { nlRcphPolicyItemsV1_0_0 } from './v1_0_0/policy-items.ts'
import { nlRcph_V1_0_0_StaticFilesIds } from './v1_0_0/static-files.ts'

export const nlRcphProductName = 'product-nl-rcp-hiscox'
export type NlRcphProductName = typeof nlRcphProductName

type NlRcphProductVersionFormat = ProductVersionFormat<'nl-rcp-hiscox'>
export type NlRcphActivitiesConfigVersionFormat = ActivitiesConfigVersionFormat<NlRcphProductName>

export const nlRcphProductVersions = [
  /**
   * Initial version
   */
  'product-nl-rcp-hiscox-1.0.0-2026-05-21',
] as const satisfies NlRcphProductVersionFormat[]

export type NlRcphProductVersion = (typeof nlRcphProductVersions)[number]

type NlRcphRiskCarrierProductTypeBySegment = {
  [key in Exclude<NlRcphSegment, 'MISC_ACCOUNTANTS'>]: `nl-rcph-${(typeof nlRcphSegmentShortLabel)[key]}-v${number}`
} & {
  MISC_ACCOUNTANTS: {
    [key in NlRcphAccountantSubSegment]: `nl-rcph-${(typeof nlRcphSegmentShortLabel)['MISC_ACCOUNTANTS']}-${(typeof nlRcphAccountantSubSegmentShortLabel)[key]}-v${number}`
  }
}

export const nlRcphRiskCarrierLabelByProductVersion = {
  'product-nl-rcp-hiscox-1.0.0-2026-05-21': {
    ARCHI: 'nl-rcph-archi-v1',
    MAC: 'nl-rcph-mac-v1',
    MAN: 'nl-rcph-man-v1',
    MISC_ACCOUNTANTS: {
      Accountantskantoor: 'nl-rcph-misc-accountants-accountantskantoor-v1',
      Administratiekantoor: 'nl-rcph-misc-accountants-administratiekantoor-v1',
    },
    MISC_GENERAL: 'nl-rcph-misc-general-v1',
    MISC_LEGAL: 'nl-rcph-misc-legal-v1',
    SECU: 'nl-rcph-secu-v1',
    TECH: 'nl-rcph-tech-v1',
  },
} as const satisfies Record<NlRcphProductVersion, NlRcphRiskCarrierProductTypeBySegment>

export const nlRcphRiskCarrierProductTypeNames = [
  ...Object.values(nlRcphRiskCarrierLabelByProductVersion).flatMap((segmentMapping) =>
    Object.values(segmentMapping).flatMap((value) => (typeof value === 'string' ? [value] : Object.values(value))),
  ),
] as const

export type NlRcphRiskCarrierProductType = (typeof nlRcphRiskCarrierProductTypeNames)[number]

export const allNlRcphStaticFilesIds = [...nlRcph_V1_0_0_StaticFilesIds] as const

export type NlRcphStaticFilesId = (typeof allNlRcphStaticFilesIds)[number]

export const allNlRcphStaticFilesReferences = {
  'nl-rcph-v1_0_0-base-general-terms-nl': 'HAP25.01',
  'nl-rcph-v1_0_0-base-general-terms-en': 'HAP25.01',
  'nl-rcph-v1_0_0-cs-rcp-mac-general-terms-nl': 'HMR25.01',
  'nl-rcph-v1_0_0-cs-rcp-mac-general-terms-en': 'HMR25.01',
  'nl-rcph-v1_0_0-cs-rcp-man-general-terms-nl': 'HMC25.01',
  'nl-rcph-v1_0_0-cs-rcp-man-general-terms-en': 'HMC25.01',
  'nl-rcph-v1_0_0-cs-rcp-misc-general-general-terms-nl': 'HBA25.01',
  'nl-rcph-v1_0_0-cs-rcp-misc-general-general-terms-en': 'HBA25.01',
  'nl-rcph-v1_0_0-cs-rcp-tech-general-terms-nl': 'HIT25.01',
  'nl-rcph-v1_0_0-cs-rcp-tech-general-terms-en': 'HIT25.01',
  'nl-rcph-v1_0_0-cs-rcp-misc-legal-general-terms-nl': 'MPH-2013B',
  'nl-rcph-v1_0_0-cs-rcp-misc-legal-general-terms-en': 'MPH-2013B',
  'nl-rcph-v1_0_0-cs-rcp-misc-accountants-general-terms-nl': 'MPH-2013B',
  'nl-rcph-v1_0_0-cs-rcp-misc-accountants-general-terms-en': 'MPH-2013B',
  'nl-rcph-v1_0_0-cs-rcp-secu-general-terms-nl': 'PSC-HBAV08B',
  'nl-rcph-v1_0_0-cs-rcp-secu-general-terms-en': 'PSC-HBAV08B',
  'nl-rcph-v1_0_0-cs-rcp-archi-general-terms-nl': 'PSC-HBA-AE-06B',
  'nl-rcph-v1_0_0-cs-rcp-archi-general-terms-en': 'PSC-HBA-AE-06B',
  'nl-rcph-v1_0_0-cs-rce-mac-man-tech-misc-general-terms-nl': 'HAVB25.01',
  'nl-rcph-v1_0_0-cs-rce-mac-man-tech-misc-general-terms-en': 'HAVB25.01',
  'nl-rcph-v1_0_0-cs-rce-legal-acc-archi-secu-general-terms-nl': 'PSC-HAVB08B',
  'nl-rcph-v1_0_0-cs-rce-legal-acc-archi-secu-general-terms-en': 'PSC-HAVB08B',
  'nl-rcph-v1_0_0-cs-cyber-mac-man-tech-misc-general-terms-nl': 'HCR25.01',
  'nl-rcph-v1_0_0-cs-cyber-mac-man-tech-misc-general-terms-en': 'HCR25.01',
  'nl-rcph-v1_0_0-cs-cyber-legal-acc-archi-secu-general-terms-nl': 'HCC-2022/01',
  'nl-rcph-v1_0_0-cs-cyber-legal-acc-archi-secu-general-terms-en': 'HCC-2022/01',
  'nl-rcph-v1_0_0-ipid-mac': 'IPID MAC',
  'nl-rcph-v1_0_0-ipid-man': 'IPID MAN',
  'nl-rcph-v1_0_0-ipid-misc': 'IPID MISC',
  'nl-rcph-v1_0_0-ipid-tech': 'IPID TECH',
  'nl-rcph-v1_0_0-ipid-misc-legal': 'IPID MISC_LEGAL',
  'nl-rcph-v1_0_0-ipid-misc-accountants': 'IPID MISC_ACCOUNTANTS',
  'nl-rcph-v1_0_0-ipid-secu': 'IPID SECU',
  'nl-rcph-v1_0_0-ipid-archi': 'IPID ARCHI',
  'nl-rcph-v1_0_0-ipid-rce-mac-man-tech-misc': 'IPID RCE MAC / MAN / TECH / MISC',
  'nl-rcph-v1_0_0-ipid-rce-legal-acc-archi-secu': 'IPID RCE LEGAL / ACC / ARCHI / SECU',
  'nl-rcph-v1_0_0-ipid-cyber-mac-man-tech-misc': 'IPID Cyber MAC / MAN / TECH / MISC',
  'nl-rcph-v1_0_0-ipid-cyber-legal-acc-archi-secu': 'IPID Cyber LEGAL / ACC / ARCHI / SECU',
} as const satisfies Record<NlRcphStaticFilesId, string>

// NL-RCPH general terms PDFs already include footers; IPIDs do not need one.
export const nlRcphStaticFileFooters = {
  'nl-rcph-v1_0_0-base-general-terms-nl': undefined,
  'nl-rcph-v1_0_0-base-general-terms-en': undefined,
  'nl-rcph-v1_0_0-cs-rcp-mac-general-terms-nl': undefined,
  'nl-rcph-v1_0_0-cs-rcp-mac-general-terms-en': undefined,
  'nl-rcph-v1_0_0-cs-rcp-man-general-terms-nl': undefined,
  'nl-rcph-v1_0_0-cs-rcp-man-general-terms-en': undefined,
  'nl-rcph-v1_0_0-cs-rcp-misc-general-general-terms-nl': undefined,
  'nl-rcph-v1_0_0-cs-rcp-misc-general-general-terms-en': undefined,
  'nl-rcph-v1_0_0-cs-rcp-tech-general-terms-nl': undefined,
  'nl-rcph-v1_0_0-cs-rcp-tech-general-terms-en': undefined,
  'nl-rcph-v1_0_0-cs-rcp-misc-legal-general-terms-nl': undefined,
  'nl-rcph-v1_0_0-cs-rcp-misc-legal-general-terms-en': undefined,
  'nl-rcph-v1_0_0-cs-rcp-misc-accountants-general-terms-nl': undefined,
  'nl-rcph-v1_0_0-cs-rcp-misc-accountants-general-terms-en': undefined,
  'nl-rcph-v1_0_0-cs-rcp-secu-general-terms-nl': undefined,
  'nl-rcph-v1_0_0-cs-rcp-secu-general-terms-en': undefined,
  'nl-rcph-v1_0_0-cs-rcp-archi-general-terms-nl': undefined,
  'nl-rcph-v1_0_0-cs-rcp-archi-general-terms-en': undefined,
  'nl-rcph-v1_0_0-cs-rce-mac-man-tech-misc-general-terms-nl': undefined,
  'nl-rcph-v1_0_0-cs-rce-mac-man-tech-misc-general-terms-en': undefined,
  'nl-rcph-v1_0_0-cs-rce-legal-acc-archi-secu-general-terms-nl': undefined,
  'nl-rcph-v1_0_0-cs-rce-legal-acc-archi-secu-general-terms-en': undefined,
  'nl-rcph-v1_0_0-cs-cyber-mac-man-tech-misc-general-terms-nl': undefined,
  'nl-rcph-v1_0_0-cs-cyber-mac-man-tech-misc-general-terms-en': undefined,
  'nl-rcph-v1_0_0-cs-cyber-legal-acc-archi-secu-general-terms-nl': undefined,
  'nl-rcph-v1_0_0-cs-cyber-legal-acc-archi-secu-general-terms-en': undefined,
  'nl-rcph-v1_0_0-ipid-mac': undefined,
  'nl-rcph-v1_0_0-ipid-man': undefined,
  'nl-rcph-v1_0_0-ipid-misc': undefined,
  'nl-rcph-v1_0_0-ipid-tech': undefined,
  'nl-rcph-v1_0_0-ipid-misc-legal': undefined,
  'nl-rcph-v1_0_0-ipid-misc-accountants': undefined,
  'nl-rcph-v1_0_0-ipid-secu': undefined,
  'nl-rcph-v1_0_0-ipid-archi': undefined,
  'nl-rcph-v1_0_0-ipid-rce-mac-man-tech-misc': undefined,
  'nl-rcph-v1_0_0-ipid-rce-legal-acc-archi-secu': undefined,
  'nl-rcph-v1_0_0-ipid-cyber-mac-man-tech-misc': undefined,
  'nl-rcph-v1_0_0-ipid-cyber-legal-acc-archi-secu': undefined,
} as const satisfies Partial<Record<NlRcphStaticFilesId, string | undefined>>

export const nlRcphStaticFilesNameByStaticFileId = {
  'nl-rcph-v1_0_0-base-general-terms-nl': 'CG generiques - Zakelijke verzekeringen Algemene polisvoorwaarden',
  'nl-rcph-v1_0_0-base-general-terms-en': 'CG generiques - Zakelijke verzekeringen Algemene polisvoorwaarden',
  'nl-rcph-v1_0_0-cs-rcp-mac-general-terms-nl': 'CS RCP MAC',
  'nl-rcph-v1_0_0-cs-rcp-mac-general-terms-en': 'CS RCP MAC',
  'nl-rcph-v1_0_0-cs-rcp-man-general-terms-nl': 'CS RCP MAN',
  'nl-rcph-v1_0_0-cs-rcp-man-general-terms-en': 'CS RCP MAN',
  'nl-rcph-v1_0_0-cs-rcp-misc-general-general-terms-nl': 'CS RCP MISC_GENERAL',
  'nl-rcph-v1_0_0-cs-rcp-misc-general-general-terms-en': 'CS RCP MISC_GENERAL',
  'nl-rcph-v1_0_0-cs-rcp-tech-general-terms-nl': 'CS RCP TECH',
  'nl-rcph-v1_0_0-cs-rcp-tech-general-terms-en': 'CS RCP TECH',
  'nl-rcph-v1_0_0-cs-rcp-misc-legal-general-terms-nl': 'CS RCP MISC_LEGAL',
  'nl-rcph-v1_0_0-cs-rcp-misc-legal-general-terms-en': 'CS RCP MISC_LEGAL',
  'nl-rcph-v1_0_0-cs-rcp-misc-accountants-general-terms-nl': 'CS RCP MISC_ACCOUNTANTS',
  'nl-rcph-v1_0_0-cs-rcp-misc-accountants-general-terms-en': 'CS RCP MISC_ACCOUNTANTS',
  'nl-rcph-v1_0_0-cs-rcp-secu-general-terms-nl': 'CS RCP SECU',
  'nl-rcph-v1_0_0-cs-rcp-secu-general-terms-en': 'CS RCP SECU',
  'nl-rcph-v1_0_0-cs-rcp-archi-general-terms-nl': 'CS RCP ARCHI',
  'nl-rcph-v1_0_0-cs-rcp-archi-general-terms-en': 'CS RCP ARCHI',
  'nl-rcph-v1_0_0-cs-rce-mac-man-tech-misc-general-terms-nl': 'CS RCE MAC / MAN / TECH / MISC',
  'nl-rcph-v1_0_0-cs-rce-mac-man-tech-misc-general-terms-en': 'CS RCE MAC / MAN / TECH / MISC',
  'nl-rcph-v1_0_0-cs-rce-legal-acc-archi-secu-general-terms-nl': 'CS RCE LEGAL / ACC / ARCHI / SECU',
  'nl-rcph-v1_0_0-cs-rce-legal-acc-archi-secu-general-terms-en': 'CS RCE LEGAL / ACC / ARCHI / SECU',
  'nl-rcph-v1_0_0-cs-cyber-mac-man-tech-misc-general-terms-nl': 'CS Cyber MAC / MAN / TECH / MISC',
  'nl-rcph-v1_0_0-cs-cyber-mac-man-tech-misc-general-terms-en': 'CS Cyber MAC / MAN / TECH / MISC',
  'nl-rcph-v1_0_0-cs-cyber-legal-acc-archi-secu-general-terms-nl': 'CS Cyber LEGAL / ACC / ARCHI / SECU',
  'nl-rcph-v1_0_0-cs-cyber-legal-acc-archi-secu-general-terms-en': 'CS Cyber LEGAL / ACC / ARCHI / SECU',
  'nl-rcph-v1_0_0-ipid-mac': 'IPID MAC',
  'nl-rcph-v1_0_0-ipid-man': 'IPID MAN',
  'nl-rcph-v1_0_0-ipid-misc': 'IPID MISC',
  'nl-rcph-v1_0_0-ipid-tech': 'IPID TECH',
  'nl-rcph-v1_0_0-ipid-misc-legal': 'IPID MISC_LEGAL',
  'nl-rcph-v1_0_0-ipid-misc-accountants': 'IPID MISC_ACCOUNTANTS',
  'nl-rcph-v1_0_0-ipid-secu': 'IPID SECU',
  'nl-rcph-v1_0_0-ipid-archi': 'IPID ARCHI',
  'nl-rcph-v1_0_0-ipid-rce-mac-man-tech-misc': 'IPID RCE MAC / MAN / TECH / MISC',
  'nl-rcph-v1_0_0-ipid-rce-legal-acc-archi-secu': 'IPID RCE LEGAL / ACC / ARCHI / SECU',
  'nl-rcph-v1_0_0-ipid-cyber-mac-man-tech-misc': 'IPID Cyber MAC / MAN / TECH / MISC',
  'nl-rcph-v1_0_0-ipid-cyber-legal-acc-archi-secu': 'IPID Cyber LEGAL / ACC / ARCHI / SECU',
} as const satisfies Record<NlRcphStaticFilesId, string>

export const nlRcphStaticFilesIdsByProductVersion = {
  'product-nl-rcp-hiscox-1.0.0-2026-05-21': nlRcph_V1_0_0_StaticFilesIds,
} as const satisfies Record<NlRcphProductVersion, readonly NlRcphStaticFilesId[]>

export const nlRcphProduct_V_1_0_0 = {
  version: 'product-nl-rcp-hiscox-1.0.0-2026-05-21',
  description: 'Version initiale du produit NL RCPH',
  policyItems: nlRcphPolicyItemsV1_0_0,
  staticFilesId: [...nlRcph_V1_0_0_StaticFilesIds],
  pricingVersion: ['pricing-nl-rcp-hiscox-1.0.0-2026-05-21'],
  activitiesConfigVersion: 'activities-config-product-nl-rcp-hiscox-1.0.0-2026-05-21',
} as const satisfies InsuranceProductVersion<
  NlRcphProductName,
  NlRcphProductName,
  NlRcphProductVersion,
  NlRcphPolicyItems,
  NlRcphStaticFilesId,
  NlRcphPricingVersion,
  NlRcphActivitiesConfigVersionFormat,
  NlRcphPolicyItemsState
>

export const nlRcphProduct = {
  id: 'nl-rcph',
  versions: {
    'product-nl-rcp-hiscox-1.0.0-2026-05-21': nlRcphProduct_V_1_0_0,
  },
} as const satisfies InsuranceProduct<
  NlRcphProductName,
  NlRcphProductName,
  NlRcphProductVersion,
  NlRcphPolicyItems,
  NlRcphStaticFilesId,
  NlRcphPricingVersion,
  NlRcphActivitiesConfigVersionFormat,
  NlRcphPolicyItemsState
>

export type NlRcphProduct = typeof nlRcphProduct
